import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';

import {
  Zone,
  NewZone,
  UpdateZone,
  ZoneFilters,
  ZoneDeleteResponse,
  ZoneCoverage,
} from '../models';

const zonesService = createApi({
  reducerPath: 'zonesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['Zone', 'Zones', 'ZoneCoverage'] as const,
  endpoints: (builder) => ({
    getZones: builder.query<
      PaginatedResponse<Zone>,
      {
        paginated: PaginatedRequest;
        filters?: ZoneFilters;
        search?: string;
      }
    >({
      query: ({ paginated, filters, search }) => {
        const loId = filters?.logisticOperatorId?.trim();
        return {
          url: '/zones',
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
            ...(filters?.id && { id: filters.id }),
            ...(filters?.name && { name: filters.name }),
            ...(filters?.color && { color: filters.color }),
            ...(filters?.zoneTypeId && { zoneTypeId: filters.zoneTypeId }),
            ...(loId && { logisticOperatorId: loId }),
            ...(search &&
              search.trim() !== '' && {
                search: search.trim(),
                name: search.trim(),
              }),
          },
          ...(loId
            ? {
                headers: {
                  'x-logistic-operator-id': loId,
                },
              }
            : {}),
        };
      },
      providesTags: ['Zones'],
    }),
    getZoneById: builder.query<Zone, { zoneId: string }>({
      query: ({ zoneId }) => ({
        url: `/zone/${zoneId}`,
      }),
      providesTags: ['Zone'],
    }),
    createZone: builder.mutation<Zone, NewZone>({
      query: (body) => ({
        url: '/zones',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Zones'],
    }),
    updateZone: builder.mutation<Zone, { zoneId: string; data: UpdateZone }>({
      query: ({ zoneId, data }) => ({
        url: `/zone/${zoneId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Zone', 'Zones'],
    }),
    // El backend responde 'deleted' (borrado fisico) o 'deactivated' (soft
    // delete por envios historicos): la UI lo usa para ofrecer el deshacer.
    deleteZone: builder.mutation<ZoneDeleteResponse, string>({
      query: (zoneId) => ({
        url: `/zone/${zoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Zone', 'Zones'],
    }),
    getZoneCoverage: builder.query<ZoneCoverage, { zoneId: string }>({
      query: ({ zoneId }) => ({
        url: `/zone/${zoneId}/coverage`,
      }),
      providesTags: (_result, _error, { zoneId }) => [
        { type: 'ZoneCoverage', id: zoneId },
      ],
    }),
    updateZoneCoverage: builder.mutation<
      ZoneCoverage,
      { zoneId: string; logisticOperatorIds: string[] }
    >({
      query: ({ zoneId, logisticOperatorIds }) => ({
        url: `/zone/${zoneId}/coverage`,
        method: 'PUT',
        body: { logisticOperatorIds },
      }),
      invalidatesTags: (_result, _error, { zoneId }) => [
        { type: 'ZoneCoverage', id: zoneId },
      ],
    }),
  }),
});

export default zonesService;
export const {
  useGetZonesQuery,
  useGetZoneByIdQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useGetZoneCoverageQuery,
  useUpdateZoneCoverageMutation,
} = zonesService;
